import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import {
  AccountBalanceWallet,
  QrCode2,
  Speed,
  Security,
  Public,
  Savings,
  Language,
} from '@mui/icons-material';
import { FeatureCard, AnimatedButton, GradientCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import { useHistory } from 'react-router-dom';

export const HomePage: React.FC = () => {
  const [searchHandle, setSearchHandle] = useState('');
  const history = useHistory();

  const handleSearch = () => {
    if (searchHandle.trim()) {
      history.push(`/${searchHandle.toLowerCase().replace('@', '')}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background: alpha('#6366f1', 0.05),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(90deg, ${alpha('#6366f1', 0.03)} 1px, transparent 1px),
              linear-gradient(0deg, ${alpha('#6366f1', 0.03)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" position="relative" zIndex={1}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem', lg: '4.5rem' },
                fontWeight: 900,
                mb: 3,
                lineHeight: 1.1,
              }}
            >
              Your Crypto,{' '}
              <Box
                component="span"
                sx={{
                  background: gradients.primary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                One Simple
                <br />
                Link
              </Box>
            </Typography>

            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ mb: 6, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}
            >
              Share all your cryptocurrency addresses with a single memorable handle.
              No more copying long wallet addresses.
            </Typography>

            <Box
              sx={{
                background: '#ffffff',
                borderRadius: '20px',
                p: 1,
                boxShadow: '0px 20px 60px rgba(99, 102, 241, 0.15)',
                border: '2px solid',
                borderColor: alpha('#6366f1', 0.1),
                display: 'flex',
                gap: 1,
                maxWidth: 600,
                margin: '0 auto',
                transition: 'all 0.3s ease',
                '&:focus-within': {
                  boxShadow: '0px 24px 80px rgba(99, 102, 241, 0.25)',
                  borderColor: '#6366f1',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <TextField
                fullWidth
                placeholder="Search for a handle... (e.g., @satoshi)"
                variant="outlined"
                value={searchHandle}
                onChange={(e) => setSearchHandle(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  sx: {
                    '& fieldset': { border: 'none' },
                    fontSize: '1.1rem',
                  },
                }}
              />
              <AnimatedButton
                variant="contained"
                size="large"
                onClick={handleSearch}
                sx={{
                  minWidth: 140,
                  background: gradients.primary,
                  '&:hover': {
                    background: gradients.primary,
                    opacity: 0.9,
                  },
                }}
              >
                Search
              </AnimatedButton>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <AnimatedButton variant="outlined" size="large" href="#features">
                Learn More
              </AnimatedButton>
              <AnimatedButton
                variant="text"
                size="large"
                onClick={() => history.push('/pay')}
              >
                Create Payment Request →
              </AnimatedButton>
            </Stack>

            {/* Stats */}
            <Grid container spacing={3} sx={{ mt: 8 }} justifyContent="center">
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    background: alpha('#ffffff', 0.7),
                    backdropFilter: 'blur(20px)',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: alpha('#6366f1', 0.1),
                  }}
                >
                  <Typography variant="h3" fontWeight={800} color="primary.main">
                    32+
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Cryptocurrencies
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    background: alpha('#ffffff', 0.7),
                    backdropFilter: 'blur(20px)',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: alpha('#6366f1', 0.1),
                  }}
                >
                  <Typography variant="h3" fontWeight={800} color="primary.main">
                    1000+
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Active Users
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    background: alpha('#ffffff', 0.7),
                    backdropFilter: 'blur(20px)',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: alpha('#6366f1', 0.1),
                  }}
                >
                  <Typography variant="h3" fontWeight={800} color="primary.main">
                    24/7
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Available
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box
                  sx={{
                    background: alpha('#ffffff', 0.7),
                    backdropFilter: 'blur(20px)',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: alpha('#6366f1', 0.1),
                  }}
                >
                  <Typography variant="h3" fontWeight={800} color="primary.main">
                    100%
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Free Forever
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: 12, background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Typography
              variant="h2"
              fontWeight={800}
              gutterBottom
              sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
            >
              Why Choose{' '}
              <Box
                component="span"
                sx={{
                  background: gradients.primary,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                addrs
              </Box>
              ?
            </Typography>
            <Typography variant="h6" color="text.secondary" maxWidth={600} mx="auto">
              Everything you need to accept and manage cryptocurrency payments in one place
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<Language />}
                title="Free .pay.eth ENS"
                description="Get your own youraddrs.pay.eth ENS! When you add an ETH address, you automatically get a memorable .pay.eth ENS for free."
                gradient={gradients.secondary}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<AccountBalanceWallet />}
                title="Multi-Chain Support"
                description="Support for 32+ cryptocurrencies including Bitcoin, Ethereum, and all major tokens across multiple blockchains."
                gradient={gradients.primary}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<QrCode2 />}
                title="QR Code Payments"
                description="Generate instant QR codes for any address. Perfect for in-person payments and mobile wallet scanning."
                gradient={gradients.ocean}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<Security />}
                title="Non-Custodial"
                description="You own your addresses. We never hold your funds or have access to your private keys."
                gradient={gradients.success}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<Public />}
                title="Easy Sharing"
                description="Share one simple link (addrs.to/yourhandle) instead of dozens of long wallet addresses."
                gradient={gradients.lavender}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FeatureCard
                icon={<Savings />}
                title="Fiat On-Ramps"
                description="Accept payments via credit card, debit card, and bank transfers with integrated fiat-to-crypto providers."
                gradient={gradients.emerald}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 12 }}>
        <Container maxWidth="md">
          <GradientCard
            sx={{
              p: 6,
              textAlign: 'center',
              background: gradients.mesh,
            }}
          >
            <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: '#fff' }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, color: '#fff' }}>
              Create your free crypto payment profile in less than 2 minutes
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: '#ffffff',
                  color: '#6366f1',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': {
                    background: alpha('#ffffff', 0.9),
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Sign Up Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => history.push('/about')}
                sx={{
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  fontWeight: 700,
                  px: 4,
                  '&:hover': {
                    borderColor: '#ffffff',
                    background: alpha('#ffffff', 0.1),
                  },
                }}
              >
                Learn More
              </Button>
            </Stack>
          </GradientCard>
        </Container>
      </Box>
    </Box>
  );
};
